import React, { useEffect, useState, useRef } from "react";
import { Link, withRouter } from "react-router-dom";

import Constants from "../helpers/Constants";

// Two logo variants — the navy/color one sits on light nav backgrounds, the
// white one is swapped in when the nav is over the dark hero ("/" landing
// while unscrolled). Bundled via webpack so edits under src/img/ are picked
// up automatically.
import logoNavy from "../img/landing/logo_growingsales.svg";
import logoWhite from "../img/landing/logo_growingsales-white.svg";

// ---------------------------------------------------------------------------
// Global Navigation Bar (GNB) — Figma node 230:17234
// Variants:
//   - default          : hero background, transparent bar
//   - scroll           : backdrop-blur + translucent #F4F5F8 after scroll
//   - over-dark        : white text variant — applied when on "/" (landing)
//                        and not yet scrolled, because the landing hero has a
//                        dark background (#0B0D0F) and the default dark text
//                        would be invisible.
//   - active-product   : mega menu with 멤버십 / POS / 고객관리 columns
//   - active-ex        : mega menu with 도입사례 (리테일 / 외식)
//
// Interaction:
//   - scroll > 8px switches to "scroll" variant
//   - hovering on 제품 / 도입사례 opens the corresponding mega panel
//   - mega panel + trigger share a shared hover region so it doesn't flicker
// ---------------------------------------------------------------------------

// 우진 (2026-04-30): 스토어 → 네이버 스마트스토어 외부링크. 가격정책/부가서비스
// 는 미구현 페이지(/pricing, /extra-service) 라우트로 — 빌드되면 자동 연결.
const LEFT_NAV = [
  { key: "product",   label: "제품",       to: "/products/food-pos",  hasMenu: "product" },
  { key: "cases",     label: "도입사례",   to: "/cases",              hasMenu: "cases"   },
  { key: "etc",       label: "부가서비스", to: "/extra-service",      hasMenu: "etc"     },
  { key: "pricing",   label: "가격정책",   to: "/pricing" },
  { key: "store",     label: "스토어",     to: "https://smartstore.naver.com/growing-sales/", external: true, chevron: true },
];

const PRODUCT_MENU = {
  columns: [
    {
      heading: "멤버십",
      items: [
        { to: "/products/membership", color: "blue", title: "온·오프라인 멤버십 통합", desc: "매장과 자사몰의 포인트·쿠폰을 하나로" },
      ],
    },
    {
      heading: "그로잉세일즈 POS",
      items: [
        { to: "/products/retail-pos", color: "green",  title: "리테일 POS", desc: "재고·매장·자사몰 실시간 동기화" },
        { to: "/products/food-pos", color: "orange", title: "외식 POS",    desc: "테이블 오더·주방 디스플레이 연동" },
      ],
    },
    {
      heading: "고객 관리",
      items: [
        { to: "/products/crm", color: "indigo", title: "CRM", desc: "세그먼트 기반 캠페인·메시지 자동화" },
      ],
    },
  ],
};

const CASES_MENU = {
  heading: "도입사례",
  items: [
    { to: "/cases?tab=retail#all-cases", color: "green",  title: "리테일", desc: "의류·잡화·뷰티 등" },
    { to: "/cases?tab=food#all-cases",   color: "orange", title: "외식",    desc: "카페·음식점·주류점 등" },
  ],
};

// 부가서비스 mega — mirrors landing/Product.js ETC_CARDS (5 services).
// Each item carries its own `icon` since the etc set doesn't share the
// product-mega's color→icon convention.
//
// 우진 (2026-04-30): 레거시 /features/* 페이지 삭제됨 → /extra-service#xxx
// 해시 앵커로 통일. 부가서비스 페이지가 빌드되면 각 항목으로 스크롤 정상 동작.
const ETC_MENU = {
  heading: "부가서비스",
  items: [
    { to: "/extra-service#kiosk",      color: "blue",   icon: "kiosk", title: "키오스크",   desc: "" },
    { to: "/extra-service#menu-order", color: "indigo", icon: "table", title: "테이블오더", desc: "" },
    { to: "/extra-service#waiting",    color: "green",  icon: "queue", title: "웨이팅",    desc: "" },
    { to: "/extra-service#kds",        color: "orange", icon: "kds",   title: "KDS",       desc: "" },
    // { to: "/extra-service#point",      color: "cyan",   icon: "point", title: "포인트 모드", desc: "" },
  ],
};

function Icon({ name }) {
  const base = { width: 20, height: 20, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" };
  switch (name) {
    case "chevron-down":
      return <svg {...base}><polyline points="6 9 12 15 18 9" /></svg>;
    case "arrow-up-right":
      return <svg {...base}><path d="M7 17L17 7M8 7h9v9" /></svg>;
    case "users":
      return <svg {...base} width="24" height="24"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>;
    case "shop":
      return <svg {...base} width="24" height="24"><path d="M3 9h18l-1 10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2L3 9z"/><path d="M8 9V5a4 4 0 0 1 8 0v4"/></svg>;
    case "utensils":
      return <svg {...base} width="24" height="24"><path d="M3 2v7c0 1.1.9 2 2 2h4V2"/><path d="M7 2v20"/><path d="M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3zm0 0v7"/></svg>;
    case "target":
      return <svg {...base} width="24" height="24"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>;
    // 부가서비스 mega — paths mirrored from landing/Product.js EtcIcon
    case "table":
      return <svg {...base} width="24" height="24" strokeWidth="1.8"><rect x="2.5" y="4" width="19" height="13" rx="2"/><path d="M8 21h8M12 17v4"/><path d="M8.5 10.5l2.5 2.5 4.5-4.5"/></svg>;
    case "kiosk":
      return <svg {...base} width="24" height="24" strokeWidth="1.8"><rect x="5" y="2" width="14" height="20" rx="2"/><path d="M8.5 6.5h7M8.5 10h5"/><rect x="8.5" y="13.5" width="7" height="5" rx="1"/></svg>;
    case "queue":
      return <svg {...base} width="24" height="24" strokeWidth="1.8"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3.5 2"/></svg>;
    case "kds":
      return <svg {...base} width="24" height="24" strokeWidth="1.8"><rect x="2.5" y="5" width="19" height="12" rx="2"/><path d="M7 21h10M12 17v4"/><path d="M8 9.5h8M8 13h5"/></svg>;
    case "point":
      return <svg {...base} width="24" height="24" strokeWidth="1.8"><rect x="2.5" y="4" width="14" height="10" rx="1.5"/><rect x="7.5" y="10" width="14" height="10" rx="1.5"/></svg>;
    default:
      return null;
  }
}

function GSLogo({ overDark }) {
  // Two pre-baked SVG variants — see import block at the top.
  //   • light nav      → logo_growingsales.svg        (Figma color, navy+yellow)
  //   • .gnb--over-dark → logo_growingsales-white.svg (solid white silhouette)
  return (
    <span className="gnb__logo" aria-label="GrowingSales">
      <img
        className="gnb__logo-mark"
        src={overDark ? logoWhite : logoNavy}
        alt=""
        aria-hidden="true"
        width="32"
        height="32"
      />
    </span>
  );
}

function MegaItem({ item, onClose }) {
  // Items can either set an explicit `icon` (used by ETC_MENU) or rely on
  // the color → icon convention used by PRODUCT_MENU / CASES_MENU.
  const iconName =
    item.icon ||
    (item.color === "blue"
      ? "users"
      : item.color === "green"
      ? "shop"
      : item.color === "orange"
      ? "utensils"
      : "target");
  return (
    <Link to={item.to} onClick={onClose} className="gnb__mega-item">
      <span className={`gnb__mega-icon gnb__mega-icon--${item.color}`}>
        <Icon name={iconName} />
      </span>
      <span className="gnb__mega-text">
        <span className="gnb__mega-title">{item.title}</span>
        <span className="gnb__mega-desc">{item.desc}</span>
      </span>
    </Link>
  );
}

function ProductMega({ onClose }) {
  return (
    <div className="gnb__mega gnb__mega--product">
      <div className="gnb__mega-inner">
        {PRODUCT_MENU.columns.map((col, i) => (
          <React.Fragment key={col.heading}>
            <div className="gnb__mega-col">
              <h5 className="gnb__mega-heading">{col.heading}</h5>
              <div className="gnb__mega-items">
                {col.items.map((it, j) => <MegaItem key={j} item={it} onClose={onClose} />)}
              </div>
            </div>
            {i < PRODUCT_MENU.columns.length - 1 && <span className="gnb__mega-divider" />}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

function CasesMega({ onClose }) {
  return (
    <div className="gnb__mega gnb__mega--cases">
      <div className="gnb__mega-inner gnb__mega-inner--ex">
        <h5 className="gnb__mega-heading">{CASES_MENU.heading}</h5>
        <div className="gnb__mega-items-row">
          {CASES_MENU.items.map((it, j) => (
            <React.Fragment key={j}>
              <MegaItem item={it} onClose={onClose} />
              {j < CASES_MENU.items.length - 1 && <span className="gnb__mega-divider gnb__mega-divider--vertical" />}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}

function EtcMega({ onClose }) {
  return (
    <div className="gnb__mega gnb__mega--etc">
      <div className="gnb__mega-inner gnb__mega-inner--ex">
        <h5 className="gnb__mega-heading">{ETC_MENU.heading}</h5>
        <div className="gnb__mega-items-row gnb__mega-items-row--etc">
          {ETC_MENU.items.map((it, j) => (
            <React.Fragment key={j}>
              <MegaItem item={it} onClose={onClose} />
              {j < ETC_MENU.items.length - 1 && (
                <span className="gnb__mega-divider gnb__mega-divider--vertical" />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}

function Navbar({ location }) {
  const [scrolled, setScrolled] = useState(false);
  const [openMenu, setOpenMenu] = useState(null); // "product" | "cases" | "etc" | null
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState(null); // "product" | "cases" | "etc" | null
  const closeTimer = useRef(null);

  // Scroll state
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close any open menu on route change
  useEffect(() => {
    setOpenMenu(null);
    setMobileOpen(false);
    setMobileExpanded(null);
  }, [location?.pathname]);

  // (2026-05-06) 모바일 드로어 열렸을 때 뒤 페이지 스크롤 잠금.
  // 단순히 body overflow:hidden 만 적용 — position:fixed 트릭은 닫을 때
  // body 가 잠깐 top:0 으로 점프했다가 scrollTo 로 복귀하며 시각적 깜빡임이
  // 발생함. overflow:hidden 만 쓰면 body 위치가 바뀌지 않아 깜빡임 없음.
  // (iOS Safari 에서 body 가 살짝 늘어나는 케이스가 있지만 mobile drawer
  // 사용 컨텍스트에서는 무시 가능 수준)
  useEffect(() => {
    if (!mobileOpen) return undefined;
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = original;
    };
  }, [mobileOpen]);

  const closeMobile = () => {
    setMobileOpen(false);
    setMobileExpanded(null);
  };
  const toggleMobileExpand = (key) =>
    setMobileExpanded((prev) => (prev === key ? null : key));

  const scheduleClose = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpenMenu(null), 120);
  };
  const cancelClose = () => clearTimeout(closeTimer.current);

  // (2026-05-06) 모든 nav 항목 hover 시 그 항목의 key 를 openMenu 로 set —
  // 가격정책/스토어 처럼 mega menu 가 없는 항목도 다른 메뉴들이 dim 되는
  // 인터랙션을 동일하게 받도록. mega 패널 / backdrop blur 는 별도로 hasMega
  // 조건 (아래 MEGA_MENUS) 으로 mega 있는 메뉴일 때만 렌더.
  const handleEnter = (key) => {
    cancelClose();
    setOpenMenu(key || null);
  };

  // mega menu 가 정의된 메뉴들 (이 set 에 들어있을 때만 mega panel + backdrop
  // blur + bar solid paper 효과가 활성화됨). 가격정책/스토어 hover 는 dim
  // 효과만 적용되고 mega 는 안 열림.
  const MEGA_MENUS = ["product", "cases", "etc"];
  const hasMega = MEGA_MENUS.includes(openMenu);

  // Pages whose top-of-page hero uses the dark (#0B0D0F) background. While
  // unscrolled on these routes, the navbar sits over the dark hero and needs
  // the white text/logo variant. Once the user scrolls past ~8px (or a mega
  // menu opens), the bar switches to the translucent paper variant which has
  // its own dark text on light bg. Add new dark-hero routes here.
  const DARK_HERO_ROUTES = ["/", "/products/membership", "/products/retail-pos", "/products/food-pos", "/products/crm", "/extra-service", "/pricing"];
  const isOverDarkRoute = DARK_HERO_ROUTES.includes(location?.pathname);
  // (2026-05-06) 모바일 드로어가 열리면 bar 배경을 solid white 로 바꾸므로
  // dark-hero 의 흰색 아이콘 변형이 white-on-white 가 되어 안 보임.
  // mobileOpen 시 overDark 를 false 로 하여 다크 hero 라도 기본 dark 아이콘
  // 으로 표시되게 함. hasMega (mega 열림) 시에도 false — bar 가 solid paper
  // 로 바뀌므로 흰색 텍스트는 안 보임. 가격정책/스토어 hover (mega 없음) 는
  // overDark 유지 — bar 가 transparent 그대로라 흰색 텍스트 정상 표시.
  const overDark = isOverDarkRoute && !scrolled && !hasMega && !mobileOpen;

  const classList = [
    "gnb",
    scrolled ? "gnb--scrolled" : "gnb--default",
    hasMega ? "gnb--open" : "",
    mobileOpen ? "gnb--mobile-open" : "",
    overDark ? "gnb--over-dark" : "",
  ].filter(Boolean).join(" ");

  return (
    <header className={classList} onMouseLeave={scheduleClose}>
      <div className="gnb__bar">
        <div className="gnb__bar-inner">
          <Link to="/" className="gnb__brand" onClick={() => setOpenMenu(null)}>
            <GSLogo overDark={overDark} />
          </Link>

          <nav className="gnb__nav gnb__nav--left" aria-label="주요 네비게이션">
            {LEFT_NAV.map((n) => {
              // (2026-05-06) hasMenu 가 아닌 n.key 로 비교 — 가격정책/스토어
              // 처럼 mega 가 없는 메뉴도 hover active/dim 동작이 일관되게 적용.
              const isActive = openMenu === n.key;
              const classBtn = [
                "gnb__btn",
                openMenu && !isActive ? "gnb__btn--dim" : "",
                isActive ? "gnb__btn--active" : "",
              ].filter(Boolean).join(" ");

              const inner = (
                <>
                  <span>{n.label}</span>
                  {n.chevron && <span className="gnb__chevron"><Icon name="arrow-up-right" /></span>}
                </>
              );

              return (
                <div
                  key={n.key}
                  className="gnb__btn-wrap"
                  onMouseEnter={() => handleEnter(n.key)}
                >
                  {n.external ? (
                    <a href={n.to} target="_blank" rel="noopener noreferrer" className={classBtn}>{inner}</a>
                  ) : (
                    <Link to={n.to} className={classBtn} onClick={() => setOpenMenu(null)}>{inner}</Link>
                  )}
                </div>
              );
            })}
          </nav>

          {/* 우진 (2026-04-30): 로그인 / 무료로 시작하기 둘 다 대시보드(외부)로
              연결. 같은 도메인이라 신규 탭으로 이동. */}
          <nav className="gnb__nav gnb__nav--right" aria-label="계정">
            <a
              href={Constants.DASHBOARD_HOME}
              target="_blank"
              rel="noopener noreferrer"
              className="gnb__btn"
            >
              로그인
            </a>
            <a
              href={Constants.DASHBOARD_HOME}
              target="_blank"
              rel="noopener noreferrer"
              className="gnb__cta"
            >
              무료로 시작하기
            </a>
          </nav>

          {/*
            (2026-05-06) Figma 954:6060 — 메뉴 열렸을 때 햄버거 → X (close)
            아이콘으로 토글. mobileOpen 상태에 따라 다른 markup 을 렌더해
            CSS transform 트릭 없이 명확하게 분기.
          */}
          <button
            className={`gnb__mobile-toggle${mobileOpen ? " is-open" : ""}`}
            aria-label={mobileOpen ? "메뉴 닫기" : "메뉴 열기"}
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((v) => !v)}
          >
            {mobileOpen ? (
              <svg
                className="gnb__mobile-toggle-icon"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M6 6L18 18M6 18L18 6" />
              </svg>
            ) : (
              <>
                <span />
                <span />
                <span />
              </>
            )}
          </button>
        </div>

        {hasMega && (
          <div
            className="gnb__mega-wrap"
            onMouseEnter={cancelClose}
            onMouseLeave={scheduleClose}
          >
            {openMenu === "product" && <ProductMega onClose={() => setOpenMenu(null)} />}
            {openMenu === "cases" && <CasesMega onClose={() => setOpenMenu(null)} />}
            {openMenu === "etc" && <EtcMega onClose={() => setOpenMenu(null)} />}
          </div>
        )}
      </div>

      {/*
        (2026-05-06) Figma 185:11028 — 메가 메뉴 열렸을 때 body 전체에
        backdrop-blur 10px + rgba(0,0,0,0.3) overlay 적용. GNB 의 stacking
        context 안에 두되 z-index:-1 로 bar/mega-wrap 보다 뒤, 페이지
        콘텐츠보다는 위 레이어로 깔림. 클릭하면 메뉴 닫힘.
        가격정책/스토어처럼 mega 가 없는 메뉴 hover 시엔 backdrop 표시 안 함.
      */}
      {hasMega && (
        <div
          className="gnb__backdrop"
          onClick={() => setOpenMenu(null)}
          aria-hidden="true"
        />
      )}

      {/* Mobile drawer — Figma 954:6060 (collapsed) / 954:7047 (제품 expanded).
          Full-viewport overlay with expandable groups + sticky bottom CTA. */}
      {mobileOpen && (
        <div className="gnb__mobile">
          <ul className="gnb__mobile-menu">
            {LEFT_NAV.map((n) => {
              // 우진 (2026-04-30): 우측 chevron 은 expandable 항목
              // (제품/도입사례/부가서비스)에만 표시. 가격정책/스토어/
              // 로그인 등 단순 링크는 chevron 없음 (Figma 954:6060/7047).
              if (n.external) {
                return (
                  <li key={n.key}>
                    <a
                      href={n.to}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="gnb__mobile-item"
                      onClick={closeMobile}
                    >
                      <span className="gnb__mobile-item-label">
                        {n.label}
                        {n.chevron && (
                          <span
                            className="gnb__mobile-item-ext"
                            aria-hidden="true"
                          >
                            <Icon name="arrow-up-right" />
                          </span>
                        )}
                      </span>
                    </a>
                  </li>
                );
              }
              if (n.hasMenu) {
                const expanded = mobileExpanded === n.key;
                return (
                  <li key={n.key} className="gnb__mobile-row-wrap">
                    <button
                      type="button"
                      className={`gnb__mobile-item gnb__mobile-item--toggle${
                        expanded ? " is-open" : ""
                      }`}
                      aria-expanded={expanded}
                      onClick={() => toggleMobileExpand(n.key)}
                    >
                      <span className="gnb__mobile-item-label">{n.label}</span>
                      <span className="gnb__mobile-item-chevron">
                        <ChevronDown />
                      </span>
                    </button>
                    {expanded && (
                      <div className="gnb__mobile-detail">
                        {renderMobileDetail(n.hasMenu, closeMobile)}
                      </div>
                    )}
                  </li>
                );
              }
              return (
                <li key={n.key}>
                  <Link
                    to={n.to}
                    className="gnb__mobile-item"
                    onClick={closeMobile}
                  >
                    <span className="gnb__mobile-item-label">{n.label}</span>
                  </Link>
                </li>
              );
            })}
            <li className="gnb__mobile-divider" aria-hidden="true" />
            <li>
              {/* 우진 (2026-04-30): 모바일도 데스크탑과 동일하게 대시보드(외부) 로 연결. */}
              <a
                href={Constants.DASHBOARD_HOME}
                target="_blank"
                rel="noopener noreferrer"
                className="gnb__mobile-item"
                onClick={closeMobile}
              >
                <span className="gnb__mobile-item-label">로그인</span>
              </a>
            </li>
          </ul>

          <div className="gnb__mobile-cta-row">
            <a
              href={Constants.DASHBOARD_HOME}
              target="_blank"
              rel="noopener noreferrer"
              className="gnb__mobile-cta"
              onClick={closeMobile}
            >
              무료로 시작하기
            </a>
          </div>
        </div>
      )}
    </header>
  );
}

// ---------------------------------------------------------------------------
// Mobile drawer helpers — chevron icon + expanded detail panel renderer.
// Per Figma 954:7047, expanded panel has bg #F4F5F8, gap-16 between groups,
// each group: tertiary heading + secondary items.
// ---------------------------------------------------------------------------

// (2026-05-06) Figma 954:6432 — 모바일 메뉴 chevron 은 기본 ↓ (아래),
// active(expanded) 시 ↑ (위). 회전은 SCSS 의 .is-open 클래스에서 처리.
function ChevronDown() {
  return (
    <svg
      className="gnb__mobile-chevron"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function renderMobileDetail(menuKey, onItemClick) {
  // 제품만 sub-group heading(멤버십 / 그로잉세일즈 POS / 고객 관리)이 있음.
  // 도입사례 / 부가서비스는 상위 메뉴 라벨과 동일한 heading을 또 노출할
  // 필요가 없어서(우진 2026-04-30) heading 없이 items만 렌더.
  if (menuKey === "product") {
    return PRODUCT_MENU.columns.map((col, i) => (
      <div key={i} className="gnb__mobile-detail-group">
        <p className="gnb__mobile-detail-heading">{col.heading}</p>
        <ul className="gnb__mobile-detail-list">
          {col.items.map((it) => (
            <li key={it.to}>
              <Link
                to={it.to}
                className="gnb__mobile-detail-item"
                onClick={onItemClick}
              >
                {it.title}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    ));
  }
  const flat = menuKey === "cases" ? CASES_MENU : ETC_MENU;
  return (
    <ul className="gnb__mobile-detail-list">
      {flat.items.map((it) => (
        <li key={it.to}>
          <Link
            to={it.to}
            className="gnb__mobile-detail-item"
            onClick={onItemClick}
          >
            {it.title}
          </Link>
        </li>
      ))}
    </ul>
  );
}

export default withRouter(Navbar);
